import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CemeteryRequest from "@/components/CemeteryRequest";
import { toast } from "sonner";
import { CreditCard, Landmark, Wallet, CheckCircle2 } from "lucide-react";
import api, { apiError, uploadPhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, Loading, Field } from "@/components/common";
import BackNav from "@/components/BackNav";
import CemeteryMap from "@/components/CemeteryMap";
import PayPalCheckout from "@/components/PayPalCheckout";
import WhatsAppLink from "@/components/WhatsAppLink";
import { Banknote } from "lucide-react";

const METHODS = [
  { id: "carta", label: "Carta", icon: CreditCard, hint: "Visa e Mastercard, anche senza conto PayPal" },
  { id: "paypal", label: "PayPal", icon: Wallet, hint: "Paga con il tuo conto PayPal" },
  { id: "bonifico", label: "Bonifico bancario", icon: Landmark, hint: "Dati bancari mostrati dopo la conferma dell'ordine" },
  { id: "contanti", label: "Contanti", icon: Banknote, hint: "Concordiamo insieme pagamento e appuntamento" },
];

export default function Campagna() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [camp, setCamp] = useState(null);
  const [productId, setProductId] = useState("");
  const [delegation, setDelegation] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [cemeteries, setCemeteries] = useState([]);
  const [comune, setComune] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [posConfirmed, setPosConfirmed] = useState(false);
  const [done, setDone] = useState(null);

  // Conferma ordine (bonifico/contanti/PayPal completato): vista nuova nella stessa rotta → riparti dall'alto.
  useEffect(() => {
    if (done) window.scrollTo(0, 0);
  }, [done]);
  const [pending, setPending] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    deceased_name: "", deceased_surname: "", deceased_info: "",
    cemetery_id: "", campo: "", fila: "", numero: "", reference: "",
    notes: "", preferred_date: "", payment_method: "carta",
    photos: [], map_x: null, map_y: null,
  });

  useEffect(() => {
    const rec = searchParams.get("ricorrenza");
    api.get(`/campaigns/active${rec ? `?rec=${rec}` : ""}`).then((r) => { setCamp(r.data || null); setLoaded(true); }).catch(() => setLoaded(true));
    api.get("/cemeteries").then((r) => {
      setCemeteries(r.data);
      if (r.data.length > 0) {
        setComune(r.data[0].comune);
        setForm((f) => ({ ...f, cemetery_id: r.data[0].id }));
      }
    });
  }, []);

  if (!loaded) return <Loading />;

  if (!camp) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="campaign-ended">
        <BackNav />
        <h1 className="font-serif text-4xl text-navy mt-6 mb-4">La campagna è terminata</h1>
        <p className="text-body mb-8">L'offerta speciale non è più disponibile. Puoi comunque prenderti cura del luogo dei tuoi cari con i nostri servizi.</p>
        <Link to="/abbonamenti" className="btn-primary inline-block" data-testid="campaign-ended-subscriptions">Scopri gli abbonamenti</Link>
      </div>
    );
  }

  const comuni = [...new Set(cemeteries.map((c) => c.comune))];
  const selProduct = camp?.products?.find((p) => p.id === productId) || camp?.products?.[0] || null;
  const offerPrice = selProduct?.price ?? camp?.price;
  const offerName = selProduct?.name ?? camp?.product_name;
  const cemeteriesOfComune = cemeteries.filter((c) => c.comune === comune);
  const cemetery = cemeteries.find((c) => c.id === form.cemetery_id);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onComune = (e) => {
    setComune(e.target.value);
    const first = cemeteries.find((c) => c.comune === e.target.value);
    if (first) setForm((f) => ({ ...f, cemetery_id: first.id }));
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadPhoto(f));
      setForm((f) => ({ ...f, photos: [...f.photos, ...urls] }));
      toast.success("Fotografie caricate.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const formValid = form.deceased_name.trim() && form.deceased_surname.trim() && form.cemetery_id && posConfirmed;

  const METHOD_LABELS = { carta: "Carta (online)", paypal: "PayPal", bonifico: "Bonifico bancario", contanti: "CONTANTI" };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiato.`);
    } catch {
      toast.error("Copia non riuscita.");
    }
  };

  // Riepilogo ordine per WhatsApp: coerente con il metodo scelto (contanti/bonifico/online).
  const orderMsg = done
    ? [
        "Riepilogo ordine SEMPRE ACCANTO:",
        `Numero ordine: ${(done.payment?.id || "").slice(0, 8).toUpperCase()}`,
        `Nome e cognome: ${done.payment?.customer_name || ""}`,
        done.payment?.customer_phone ? `Telefono: ${done.payment.customer_phone}` : null,
        `Servizio: ${camp?.title || "Campagna"}`,
        "Quantità: 1",
        `Importo totale: ${eur(done.payment?.amount ?? camp?.price)}`,
        `Metodo di pagamento: ${METHOD_LABELS[done.payment?.method] || ""}`,
        `Stato pagamento: ${done.payment?.status === "completato" ? "PAGATO" : done.payment?.method === "contanti" ? "DA RISCUOTERE" : "IN ATTESA DI BONIFICO"}`,
        `Persona ricordata: ${form.deceased_name} ${form.deceased_surname}`,
        `Luogo: ${cemetery?.name || ""}${cemetery?.comune ? ` — ${cemetery.comune}` : ""}`,
        form.notes ? `Note: ${form.notes}` : null,
        done.payment?.method === "bonifico" ? `Beneficiario: ${config?.bank_holder || ""}` : null,
        done.payment?.method === "bonifico" ? `IBAN: ${config?.bank_iban || ""}` : null,
        done.payment?.method === "bonifico" ? `Causale: ${done.payment?.causale || ""}` : null,
        done.payment?.method === "contanti" ? "Vorrei concordare il pagamento in contanti e l'appuntamento. Grazie." : null,
      ].filter(Boolean).join("\n")
    : "";

  const declareBonifico = async () => {
    try {
      await api.post(`/customer/payments/${done.payment.id}/declare-bonifico`);
      setDone({ ...done, payment: { ...done.payment, status: "in_verifica" } });
      toast.success("Grazie! Verificheremo l'accredito e attiveremo il servizio.");
    } catch {
      toast.error("Qualcosa non ha funzionato. Riprova.");
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/customer/campaigns/${camp.id}/order`, { ...form, position_confirmed: posConfirmed, product_id: selProduct?.id || "", delegation });
      if (form.payment_method === "bonifico" || form.payment_method === "contanti") {
        setDone(data);
      } else {
        setPending(data);
      }
      setStep(3);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="campaign-page">
      <div className="mb-6"><BackNav /></div>

      <div className="rounded-3xl overflow-hidden border border-[#d8c9a3] bg-[#faf6ec] mb-8">
        <img src={camp.image} alt={camp.name} className="w-full h-56 sm:h-72 object-cover object-center" data-testid="campaign-hero-image" />
        <div className="p-6 sm:p-8">
          <p className="eyebrow mb-2">{camp.name}</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-navy mb-2">{camp.title}</h1>
          <p className="text-body text-sm sm:text-base mb-4">{camp.subtitle}</p>
          <div className="flex items-baseline gap-3 flex-wrap">
            <p className="font-serif text-5xl text-[#8a6d2f] leading-none" data-testid="campaign-price">{eur(offerPrice)}</p>
            <p className="text-sm font-semibold text-navy">Consegna inclusa, direttamente al cimitero con coprivaso o piattino.</p>
          </div>
          <p className="text-xs text-muted-soft mt-2" data-testid="campaign-illustrative-note">Immagine a scopo illustrativo. Il coprivaso reale potrebbe essere diverso.</p>
          {camp.products?.length > 1 && (
            <div className="flex gap-2 flex-wrap mt-4" data-testid="campaign-products">
              {camp.products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProductId(p.id)}
                  data-testid={`campaign-product-${p.id}`}
                  className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${selProduct?.id === p.id ? "border-[#4a7c59] bg-[#e8f0eb] text-navy" : "border-[#e2e8f0] bg-white text-body"}`}
                >
                  {p.name} · {eur(p.price)}
                </button>
              ))}
            </div>
          )}
          <div className="rounded-2xl border-2 border-[#4a7c59] bg-[#e8f0eb] p-5 mt-5 space-y-2" data-testid="campaign-credit-promo">
            <p className="font-bold text-[#2f5d3e] uppercase tracking-wide text-sm">
              Acquista {offerName} per {camp.name} e trasformalo in credito!
            </p>
            <p className="text-sm text-body leading-relaxed">
              Se sottoscrivi un abbonamento entro 10 giorni, l'intero importo dell'offerta ({eur(offerPrice)}) ti verrà
              riconosciuto come credito sull'abbonamento. Una sola volta per account cliente.
            </p>
          </div>
        </div>
      </div>

      {!user && (
        <div className="card-soft p-8 text-center space-y-4" data-testid="campaign-login-required">
          <p className="text-body">Per acquistare {offerName} accedi o crea gratuitamente il tuo account: ti servirà per seguire la consegna e ricevere la fotografia del servizio.</p>
          <Link to="/accedi" state={{ from: "/campagna" }} data-testid="campaign-login-cta" className="btn-primary inline-block">
            Acquista ora
          </Link>
          <p className="text-xs text-muted-soft">La registrazione è gratuita e richiede un minuto.</p>
        </div>
      )}

      {user && step === 1 && (
        <div className="space-y-8" data-testid="campaign-form">
          <div className="card-soft p-6 space-y-4">
            <p className="eyebrow">La persona ricordata</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome" testid="camp-field-deceased-name">
                <input data-testid="camp-deceased-name" className="input-soft" value={form.deceased_name} onChange={set("deceased_name")} />
              </Field>
              <Field label="Cognome" testid="camp-field-deceased-surname">
                <input data-testid="camp-deceased-surname" className="input-soft" value={form.deceased_surname} onChange={set("deceased_surname")} />
              </Field>
            </div>
            <Field label="Informazioni utili per identificare la persona (facoltative)" testid="camp-field-deceased-info">
              <input data-testid="camp-deceased-info" className="input-soft" value={form.deceased_info} onChange={set("deceased_info")} placeholder="Es. foto con la cornice bianca, accanto alla fontanella…" />
            </Field>
          </div>

          <div className="card-soft p-6 space-y-4">
            <p className="eyebrow">Il cimitero</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Comune" testid="camp-field-comune">
                <select data-testid="camp-comune" className="input-soft" value={comune} onChange={onComune}>
                  {comuni.map((cm) => (
                    <option key={cm} value={cm}>{cm}</option>
                  ))}
                </select>
              </Field>
              <Field label="Cimitero" testid="camp-field-cemetery">
                <select data-testid="camp-cemetery" className="input-soft" value={form.cemetery_id} onChange={set("cemetery_id")}>
                  {cemeteriesOfComune.map((c) => (
                    <option key={c.id} value={c.id}>{`${c.name} — ${c.address}`}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="mt-4">
              <CemeteryRequest testid="camp-cemetery-request" />
            </div>
            <Field label="Ingresso o riferimento (facoltativo)" testid="camp-field-reference">
              <input data-testid="camp-reference" className="input-soft" value={form.reference} onChange={set("reference")} placeholder="Es. ingresso secondario di via…" />
            </Field>
          </div>

          <div className="card-soft p-6 space-y-4">
            <p className="eyebrow">Posizione del luogo</p>
            <CemeteryMap
              image={cemetery?.aerial_image}
              x={form.map_x}
              y={form.map_y}
              onChange={(x, y, confirmed) => {
                setForm((f) => ({ ...f, map_x: x, map_y: y }));
                setPosConfirmed(confirmed);
              }}
            />
            <div className="grid grid-cols-3 gap-4">
              <Field label="Settore/campo" testid="camp-field-campo">
                <input data-testid="camp-campo" className="input-soft" value={form.campo} onChange={set("campo")} placeholder="Es. B" />
              </Field>
              <Field label="Fila" testid="camp-field-fila">
                <input data-testid="camp-fila" className="input-soft" value={form.fila} onChange={set("fila")} placeholder="Es. 4" />
              </Field>
              <Field label="Numero" testid="camp-field-numero">
                <input data-testid="camp-numero" className="input-soft" value={form.numero} onChange={set("numero")} placeholder="Es. 12" />
              </Field>
            </div>
          </div>

          <div className="card-soft p-6 space-y-4">
            <p className="eyebrow">Fotografie del luogo</p>
            <p className="text-xs text-muted-soft">Una o più foto della lapide/luogo ci aiutano a individuarlo con precisione.</p>
            <input data-testid="camp-photos" type="file" accept="image/*" multiple onChange={onPhotos} className="text-sm text-body" />
            {uploading && <p className="text-xs text-muted-soft">Caricamento in corso…</p>}
            {form.photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {form.photos.map((p, idx) => (
                  <img key={idx} src={p} alt={`Foto ${idx + 1}`} data-testid={`camp-photo-${idx}`} className="w-full rounded-xl border border-[#e2e8f0] aspect-square object-cover" />
                ))}
              </div>
            )}
            <Field label="Note (facoltative)" testid="camp-field-notes">
              <textarea data-testid="camp-notes" className="input-soft" rows={3} value={form.notes} onChange={set("notes")} placeholder="Qualsiasi dettaglio utile per noi…" />
            </Field>
          </div>

          <div className="card-soft p-6 space-y-4">
            <p className="eyebrow">Data del servizio</p>
            <Field label={`Data preferita per la consegna (entro il ${new Date(`${camp.end_date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" })})`} testid="camp-field-date">
              <input data-testid="camp-date" type="date" className="input-soft" value={form.preferred_date} onChange={set("preferred_date")}
                min={new Date().toLocaleDateString("en-CA")} max={camp.end_date} />
            </Field>
            <p className="text-xs text-muted-soft">La data definitiva viene confermata dalla nostra organizzazione in base ai giri programmati.</p>
          </div>

          <div className="card-soft p-6 space-y-3">
            <p className="eyebrow">Pagamento</p>
            {METHODS.map((m) => (
              <label key={m.id} data-testid={`camp-method-${m.id}`} className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition-colors duration-200 ${form.payment_method === m.id ? "border-[#1e3a5f] bg-[#f0f7ff]" : "border-[#e2e8f0]"}`}>
                <input type="radio" name="camp-pay" className="accent-[#1e3a5f]" checked={form.payment_method === m.id} onChange={() => setForm({ ...form, payment_method: m.id })} />
                <m.icon size={18} className="text-navy" />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-navy">{m.label}</span>
                  <span className="block text-xs text-muted-soft">{m.hint}</span>
                </span>
              </label>
            ))}
          </div>

          <button data-testid="camp-review-btn" disabled={!formValid || uploading} onClick={() => { setStep(2); window.scrollTo(0, 0); }} className="btn-primary w-full disabled:opacity-60">
            Continua al riepilogo
          </button>
          {!formValid && (
            <p className="text-xs text-muted-soft text-center" data-testid="camp-form-hint">
              Per continuare servono nome e cognome della persona ricordata e la posizione confermata sulla mappa.
            </p>
          )}
        </div>
      )}

      {user && step === 2 && (
        <div className="space-y-6" data-testid="campaign-summary">
          <h2 className="font-serif text-3xl text-navy">Riepilogo dell'ordine</h2>
          <div className="card-soft p-6 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-soft">Prodotto</span><span className="text-navy font-medium">{offerName}</span></div>
            <div className="flex justify-between"><span className="text-muted-soft">Prezzo</span><span className="text-navy font-medium">{eur(offerPrice)}</span></div>
            <div className="flex justify-between"><span className="text-muted-soft">Consegna</span><span className="text-navy font-medium">Inclusa, al cimitero con coprivaso o piattino</span></div>
            <div className="flex justify-between"><span className="text-muted-soft">Persona ricordata</span><span className="text-navy font-medium">{form.deceased_name} {form.deceased_surname}</span></div>
            <div className="flex justify-between"><span className="text-muted-soft">Cimitero</span><span className="text-navy font-medium text-right">{cemetery?.name} — {cemetery?.comune}</span></div>
            <div className="flex justify-between"><span className="text-muted-soft">Posizione</span><span className="text-navy font-medium">Campo {form.campo || "—"}, fila {form.fila || "—"}, n. {form.numero || "—"} · posizione su mappa confermata</span></div>
            {form.reference && <div className="flex justify-between"><span className="text-muted-soft">Riferimento</span><span className="text-navy font-medium">{form.reference}</span></div>}
            <div className="flex justify-between"><span className="text-muted-soft">Fotografie</span><span className="text-navy font-medium">{form.photos.length > 0 ? `${form.photos.length} allegate` : "Nessuna"}</span></div>
            {form.notes && <div className="flex justify-between gap-4"><span className="text-muted-soft">Note</span><span className="text-navy font-medium text-right">{form.notes}</span></div>}
            <div className="flex justify-between"><span className="text-muted-soft">Data preferita</span><span className="text-navy font-medium">{form.preferred_date ? new Date(`${form.preferred_date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "Nessuna preferenza"}</span></div>
            <div className="flex justify-between"><span className="text-muted-soft">Pagamento</span><span className="text-navy font-medium capitalize">{form.payment_method}</span></div>
          </div>
          <label data-testid="camp-delegation-label" className="flex items-start gap-3 rounded-xl border border-[#e2e8f0] bg-white p-3 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="camp-delegation-checkbox"
              checked={delegation}
              onChange={(e) => setDelegation(e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 accent-[#4a7c59]"
            />
            <span className="text-xs text-body leading-relaxed">
              Confermo di delegare SEMPRE ACCANTO a intervenire presso il luogo da me indicato, per mio conto e secondo quanto ordinato.
            </span>
          </label>
          <div className="flex gap-3">
            <button data-testid="camp-edit-btn" onClick={() => setStep(1)} className="btn-secondary flex-1">Modifica i dati</button>
            <button data-testid="camp-confirm-btn" disabled={busy || !delegation} onClick={submit} className="btn-sage flex-1 disabled:opacity-60">
              {busy ? "Un momento…" : `Conferma e paga ${eur(offerPrice)}`}
            </button>
          </div>
        </div>
      )}

      {user && step === 3 && !done && pending && (
        <div className="card-soft p-6 space-y-5" data-testid="campaign-payment">
          <p className="eyebrow mb-1">Pagamento sicuro</p>
          <h2 className="font-serif text-2xl text-navy">Completa il pagamento di {eur(pending.payment?.amount)}</h2>
          <p className="text-sm text-muted-soft">Il tuo ordine è registrato: si attiva appena il pagamento è confermato.</p>
          <PayPalCheckout
            paymentId={pending.payment.id}
            amount={pending.payment.amount}
            method={form.payment_method}
            onSuccess={() => { setDone(pending); setPending(null); toast.success("Pagamento confermato."); }}
            onCancel={() => { setPending(null); setStep(2); }}
          />
          <button data-testid="camp-payment-back" onClick={() => { setPending(null); setStep(2); }} className="btn-secondary w-full">
            Torna al riepilogo
          </button>
        </div>
      )}

      {user && step === 3 && done && (
        <div className="space-y-6" data-testid="campaign-success">
          {done.payment?.method === "bonifico" && (
            <div className="card-soft p-6 text-left space-y-3" data-testid="bonifico-details">
              <p className="eyebrow">Pagamento con bonifico bancario</p>
              <p className="text-sm text-body leading-relaxed">
                Il tuo ordine resta in attesa finché non riceviamo il pagamento. Effettua il bonifico con questi dati:
              </p>
              <div className="text-sm space-y-2">
                <div className="flex justify-between gap-4"><span className="text-muted-soft">Intestatario</span><span className="font-semibold text-navy">{config?.bank_holder || "—"}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-soft">IBAN</span><span className="font-semibold text-navy">{config?.bank_iban || "—"}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-soft">Importo da pagare</span><span className="font-semibold text-navy">{eur(done.payment?.amount)}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-soft">Causale</span><span className="font-semibold text-navy text-right">{done.payment?.causale || `SEMPRE ACCANTO - ORDINE #${(done.payment?.id || "").slice(0, 8).toUpperCase()} - ${done.payment?.customer_name || ""}`}</span></div>
              </div>
              <div className="flex gap-2 pt-1 flex-wrap">
                <button type="button" data-testid="copia-iban" onClick={() => copyText(config?.bank_iban || "", "IBAN")} className="btn-secondary !py-2 !px-4 text-xs">Copia IBAN</button>
                <button type="button" data-testid="copia-causale" onClick={() => copyText(done.payment?.causale || "", "Causale")} className="btn-secondary !py-2 !px-4 text-xs">Copia causale</button>
              </div>
              <p className="text-sm text-body">Ordine registrato. Effettua il bonifico utilizzando i dati indicati sopra.</p>
              <WhatsAppLink message={orderMsg} label="Invia riepilogo su WhatsApp" testid="riepilogo-whatsapp-bonifico" className="btn-secondary !py-2.5 text-sm" />
              {done.payment?.status === "in_verifica" ? (
                <p className="text-sm text-[#1e3a5f] bg-[#f0f7ff] border border-[#dbe7f3] rounded-xl px-4 py-3" data-testid="bonifico-in-verifica">
                  Hai dichiarato di aver effettuato il bonifico: l'ordine resta in attesa di verifica e sarà confermato appena l'accredito sarà controllato.
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted-soft">Dopo aver effettuato il bonifico, segnalacelo: l'ordine resterà in attesa di verifica fino al controllo dell'accredito da parte nostra.</p>
                  <button data-testid="bonifico-declared" onClick={declareBonifico} className="btn-primary w-full">
                    Ho effettuato il bonifico
                  </button>
                </>
              )}
            </div>
          )}
          {done.payment?.method === "contanti" && (
            <div className="card-soft p-6 text-left space-y-3" data-testid="contanti-details">
              <p className="eyebrow">Pagamento in contanti</p>
              <p className="text-sm text-body leading-relaxed">
                Il tuo ordine resta in attesa. Per il pagamento in contanti è necessario concordare
                personalmente pagamento e appuntamento con noi.
              </p>
              <WhatsAppLink message={orderMsg} label="Contattaci su WhatsApp" testid="contanti-whatsapp" className="btn-primary w-full justify-center !text-white" />
              <p className="text-xs text-muted-soft">Il messaggio si apre già compilato con il riepilogo del tuo ordine: ti basta inviarlo.</p>
            </div>
          )}
          <div className="card-soft p-8 text-center space-y-4">
            <CheckCircle2 size={44} className="text-[#4a7c59] mx-auto" />
            <h2 className="font-serif text-3xl text-navy">Grazie, {user.name}.</h2>
            <p className="text-body">
              Il tuo ordine è registrato. {offerName} sarà consegnato al {done.intervention ? "cimitero indicato" : "cimitero"}
              {form.preferred_date ? ` — faremo il possibile per la data che hai scelto (${new Date(`${form.preferred_date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" })})` : ""}.
            </p>
            <p className="text-sm text-muted-soft">A servizio completato riceverai una notifica e potrai vedere la fotografia del gesto compiuto nella tua area personale.</p>
            {(done.payment?.method === "carta" || done.payment?.method === "paypal") && (
              <div>
                <WhatsAppLink message={orderMsg} label="Invia riepilogo su WhatsApp" testid="riepilogo-whatsapp" className="btn-secondary text-sm" />
              </div>
            )}
            <Link to="/area-cliente" data-testid="camp-goto-area" className="btn-primary inline-block">Vai alla tua area personale</Link>
          </div>
          <div className="rounded-3xl border border-[#d1e2d5] bg-[#e8f0eb] p-8 text-center space-y-4" data-testid="campaign-upsell">
            <p className="font-serif text-2xl text-navy">Se desideri continuare a prenderti cura di questo luogo nel tempo, puoi scoprire i nostri percorsi di cura.</p>
            <Link to="/abbonamenti" data-testid="camp-upsell-cta" className="btn-sage inline-block">Scopri gli abbonamenti</Link>
          </div>
        </div>
      )}
    </div>
  );
}
